import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@mui/material';
import UploadFile from '@mui/icons-material/UploadFile';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';
import { useHasPermission } from '@/components/PermissionGate';
import { getApiErrorMessage } from '@/lib/validationErrors';
import type { ListViewExtraProps } from '@/config/listViewExtras';

/**
 * "Import package…" on the Setup Packages list: uploads a package file exported from another
 * environment. Schema version and content hash are verified server-side; the new package lands
 * private to the importer and opens for dry-run + apply.
 */
export function SetupPackageImportButton(_: ListViewExtraProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canImport = useHasPermission('Core.SetupPackage.Import');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const parsed = JSON.parse(await file.text());
      return (await apiClient.post<{ id: string }>('/api/core/setuppackage/import', parsed)).data;
    },
    onSuccess: (imported) => {
      setError(null);
      navigate(`/core/setuppackages/${imported.id}`);
    },
    onError: (e: unknown) => {
      const message = e instanceof SyntaxError
        ? t('Core.SetupPackage.ImportNotJson', { defaultValue: 'The file is not a package file.' })
        : getApiErrorMessage(e) ?? t('common.genericError', { defaultValue: 'An error occurred' });
      setError(message);
    },
  });

  if (!canImport) return null;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) importMutation.mutate(file);
        }}
      />
      <Button
        startIcon={<UploadFile />}
        disabled={importMutation.isPending}
        onClick={() => fileInputRef.current?.click()}
        sx={{ textTransform: 'none', color: 'text.secondary' }}
      >
        {t('Core.SetupPackage.Import', { defaultValue: 'Import package…' })}
      </Button>
      {error && (
        <Alert severity="error" sx={{ ml: 1, py: 0 }} onClose={() => setError(null)}>{error}</Alert>
      )}
    </>
  );
}
